import { FC, useEffect, useMemo, useState } from 'react';
import type { SpreadsheetChange, SpreadsheetDocument } from '@libs/types';
import { Box, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { RemediationDecision } from '@libs/constants';
import { useTrans } from '@libs/i18n';

import { useScrollToSelected } from '../utils/use-scroll-to-selected';

import { Pin } from './ReviewDocumentPanes';

/** Spreadsheet cells are values, not prose — a monospace-ish stack reads better. */
const CELL_FONT = '"Segoe UI", Inter, system-ui, sans-serif';

/** How many rows to render before the grid starts asking to be scrolled. */
const MAX_VISIBLE_ROWS = 200;

type ColumnTone = 'added' | 'removed' | 'unchanged';

/**
 * Renders a remediated workbook as an actual grid.
 *
 * The AI returns spreadsheets as JSON (`sheets → columns + rows`), not as a
 * file, so this is the only place a reviewer can see the thing they are
 * approving. Showing it as a list of header names — which is what the first
 * pass did — hides both the data and the shape of the change.
 *
 * v1 remediation is **column-level only**: the AI never edits a cell or a row.
 * So the diff lives entirely in the header band — an added column is tinted and
 * pinned, a removed one is struck through — and the body is carried through
 * untouched from the upload.
 */
export const SpreadsheetPane: FC<{
  content: SpreadsheetDocument;
  changes?: SpreadsheetChange[];
  /**
   * `original` reconstructs the workbook as it was before remediation, so a
   * spreadsheet gets the same before/after reading as prose does.
   *
   * The uploaded file is not carried on the item (`sourceText` is null for
   * spreadsheets), but v1 remediates *columns only* and every change records
   * its `header`, `columnKey` and `order` — so the original column set is
   * exactly: the proposed columns, minus the additions, plus the deletions put
   * back at their recorded position.
   */
  variant?: 'proposed' | 'original';
  /** Pin numbering, shared with the change rail. */
  order?: string[];
  selectedId?: string | null;
  onSelect?: (changeId: string | null) => void;
}> = observer(({
  content,
  changes = [],
  variant = 'proposed',
  order = [],
  selectedId = null,
  onSelect,
}) => {
  const theme = useTheme();
  const { t } = useTrans();
  const [activeSheet, setActiveSheet] = useState(0);
  // A remediated column can sit far to the right of a wide sheet. The header is
  // sticky, so this only ever scrolls sideways — the grid never jumps vertically
  // and the reviewer keeps the rows they were reading. The active sheet is part
  // of the key because switching sheets is what puts the column on screen.
  const { scrollRef, targetRef } = useScrollToSelected<HTMLDivElement, HTMLTableCellElement>(
    selectedId && `${activeSheet}:${selectedId}`,
  );

  const sheet = content.sheets[activeSheet] ?? content.sheets[0];

  // Changes are addressed by sheetId + columnKey, so index them that way rather
  // than scanning the list once per header cell.
  const changeByColumn = useMemo(() => {
    const m = new Map<string, SpreadsheetChange>();
    changes.forEach((c) => m.set(`${c.sheetId}::${c.columnKey}`, c));
    return m;
  }, [changes]);

  const columns = useMemo(() => {
    const own = [...(sheet?.columns ?? [])];
    if (!sheet) return own.sort((a, b) => a.order - b.order);

    const mine = changes.filter((c) => c.sheetId === sheet.sheetId);

    // A deleted column is gone from the AI's baseline, so both variants have to
    // put it back from the change's own `header`/`order`: the original needs it
    // because it was there, and the proposed needs it so the reviewer can see
    // what the deletion takes — and see it return when they reject it, which is
    // what finalize does. Without this the column was simply absent from the
    // proposed grid and deciding the change changed nothing on screen.
    const restored = mine
      .filter((c) => c.type === 'column_deletion')
      .filter((c) => !own.some((col) => col.columnKey === c.columnKey))
      .map((c) => ({
        columnKey: c.columnKey,
        header: c.header,
        order: c.order,
        origin: 'user' as const,
      }));

    if (variant === 'proposed') {
      return [...own, ...restored].sort((a, b) => a.order - b.order);
    }

    // The original never had the AI's additions.
    const addedKeys = new Set(
      mine.filter((c) => c.type === 'column_addition').map((c) => c.columnKey),
    );
    return [...own.filter((c) => !addedKeys.has(c.columnKey)), ...restored].sort(
      (a, b) => a.order - b.order,
    );
  }, [sheet, changes, variant]);
  const rows = useMemo(
    () => [...(sheet?.rows ?? [])].sort((a, b) => a.order - b.order),
    [sheet],
  );

  // Selecting a change in the rail must open the sheet it belongs to: a column
  // on a tab the reviewer is not looking at cannot be scrolled to, and deciding
  // on a change you cannot see is the bug this whole flow avoids.
  useEffect(() => {
    if (!selectedId) return;
    const change = changes.find((c) => c.changeId === selectedId);
    if (!change) return;
    const i = content.sheets.findIndex((s) => s.sheetId === change.sheetId);
    if (i >= 0) setActiveSheet(i);
  }, [selectedId, changes, content.sheets]);

  if (!sheet) return null;

  const visibleRows = rows.slice(0, MAX_VISIBLE_ROWS);

  const toneOf = (change?: SpreadsheetChange): ColumnTone => {
    // The original is shown as it was — the diff lives on the proposed side.
    if (!change || variant === 'original') return 'unchanged';
    const rejected = change.decision === RemediationDecision.REJECTED;
    // Rejecting an addition drops the column; rejecting a deletion keeps it.
    // Either way the reviewer should see the outcome, not the proposal.
    if (change.type === 'column_addition') return rejected ? 'removed' : 'added';
    return rejected ? 'unchanged' : 'removed';
  };

  const headerBg = (tone: ColumnTone, selected: boolean) =>
    selected
      ? theme.palette.ocean[100]
      : tone === 'added'
        ? 'rgba(27,158,92,0.12)'
        : tone === 'removed'
          ? 'rgba(232,71,73,0.10)'
          : theme.palette.grey[200];

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
      {/* Sheet tabs, along the bottom in a real workbook but along the top here:
          the grid scrolls, and tabs below a scroll region get lost. */}
      {content.sheets.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
            px: 2,
            pt: 1.5,
            flexShrink: 0,
            overflowX: 'auto',
          }}
        >
          {content.sheets.map((s, i) => {
            const active = i === activeSheet;
            return (
              <Box
                key={s.sheetId}
                component="button"
                onClick={() => setActiveSheet(i)}
                sx={{
                  border: 'none',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  fontSize: 12,
                  fontWeight: 600,
                  px: 1.75,
                  py: 0.85,
                  borderRadius: '8px 8px 0 0',
                  backgroundColor: active ? theme.palette.background.paper : 'transparent',
                  color: active ? theme.palette.text.primary : theme.palette.text.secondary,
                  borderBottom: `2px solid ${active ? theme.palette.green[700] : 'transparent'}`,
                }}
              >
                {s.name}
              </Box>
            );
          })}
        </Box>
      )}

      <Box sx={{ px: { xs: 1.5, md: 2.5 }, py: 1.25, flexShrink: 0 }}>
        {/* A single-sheet workbook takes its name from the document, which the
            page header already shows — repeating it here says nothing. */}
        {content.sheets.length > 1 && (
          <Typography variant="textSM_semiBold" sx={{ display: 'block' }}>
            {sheet.name}
          </Typography>
        )}
        <Typography variant="textXS_regular" sx={{ color: 'text.tertiary' }}>
          {t('app.remediation.sheet.summary', {
            columns: columns.length,
            rows: rows.length,
          })}
        </Typography>
      </Box>

      {/* The gutter lives on this wrapper, never on the scrollport itself: a
          sticky column pins to the scrollport edge, not the padding edge, so
          padding there would let cells slide through the gap beside the frozen
          row-number column. Insetting the scrollport gives the same margin and
          keeps the freeze exact. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          display: 'flex',
          px: { xs: 1.5, md: 2.5 },
          pb: 2,
        }}
      >
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          overflowX: 'auto',
          overflowY: 'auto',
          overscrollBehaviorX: 'contain',
          WebkitOverflowScrolling: 'touch',
          border: `1px solid ${theme.palette.grey[300]}`,
          borderRadius: 1,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box
          component="table"
          sx={{
            borderCollapse: 'separate',
            borderSpacing: 0,
            fontFamily: CELL_FONT,
            fontSize: 12.5,
            // max-content lets the row grow past the viewport so the container
            // scrolls; min-width keeps a narrow sheet filling the pane.
            width: 'max-content',
            minWidth: '100%',
            // Auto layout on purpose: fixed layout ignores the per-column
            // min/max width that keeps a 40-column sheet readable.
            tableLayout: 'auto',
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Box component="thead">
            <Box component="tr">
              {/* Row-number gutter, as a spreadsheet has. */}
              <Box
                component="th"
                sx={{
                  position: 'sticky',
                  insetInlineStart: 0,
                  top: 0,
                  zIndex: 3,
                  width: 44,
                  minWidth: 44,
                  backgroundColor: theme.palette.grey[300],
                  borderBottom: `1px solid ${theme.palette.grey[400]}`,
                  borderInlineEnd: `1px solid ${theme.palette.grey[400]}`,
                  boxShadow: '2px 0 4px rgba(0,0,0,0.06)',
                }}
              />
              {columns.map((col) => {
                const change = changeByColumn.get(`${sheet.sheetId}::${col.columnKey}`);
                const tone = toneOf(change);
                const selected = Boolean(change && selectedId === change.changeId);
                return (
                  <Box
                    component="th"
                    key={col.columnKey}
                    ref={selected ? targetRef : undefined}
                    onClick={() => change && onSelect?.(change.changeId)}
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      textAlign: 'start',
                      px: 1.5,
                      py: 1.1,
                      minWidth: 150,
                      maxWidth: 320,
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      cursor: change ? 'pointer' : 'default',
                      backgroundColor: headerBg(tone, selected),
                      color: tone === 'removed' ? theme.palette.error.main : 'text.primary',
                      textDecoration: tone === 'removed' ? 'line-through' : 'none',
                      borderBottom: `1px solid ${theme.palette.grey[400]}`,
                      borderInlineEnd: `1px solid ${theme.palette.grey[300]}`,
                      outline: selected ? `2px solid ${theme.palette.ocean[700]}` : 'none',
                      outlineOffset: -2,
                    }}
                  >
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      {col.header}
                      {change && (
                        <Pin
                          n={order.indexOf(change.changeId) + 1}
                          decision={change.decision}
                          selected={selected}
                          onClick={() => onSelect?.(change.changeId)}
                        />
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Box component="tbody">
            {visibleRows.length === 0 ? (
              <Box component="tr">
                {/* GENERATE returns the column design only — there is no data yet,
                    and pretending otherwise would misrepresent what was made. */}
                <Box
                  component="td"
                  colSpan={columns.length + 1}
                  sx={{ px: 2, py: 4, textAlign: 'center', color: 'text.tertiary' }}
                >
                  {t('app.remediation.sheet.noRows')}
                </Box>
              </Box>
            ) : (
              visibleRows.map((row, rowIndex) => (
                <Box
                  component="tr"
                  key={row.rowId}
                  sx={{
                    backgroundColor:
                      rowIndex % 2 === 1 ? theme.palette.grey[100] : 'transparent',
                  }}
                >
                  <Box
                    component="td"
                    sx={{
                      position: 'sticky',
                      insetInlineStart: 0,
                      zIndex: 1,
                      textAlign: 'center',
                      fontSize: 11,
                      color: 'text.tertiary',
                      backgroundColor: theme.palette.grey[200],
                      borderBottom: `1px solid ${theme.palette.grey[300]}`,
                      borderInlineEnd: `1px solid ${theme.palette.grey[400]}`,
                      boxShadow: '2px 0 4px rgba(0,0,0,0.06)',
                    }}
                  >
                    {rowIndex + 1}
                  </Box>
                  {columns.map((col) => {
                    const change = changeByColumn.get(`${sheet.sheetId}::${col.columnKey}`);
                    const tone = toneOf(change);
                    return (
                      <Box
                        component="td"
                        key={col.columnKey}
                        sx={{
                          px: 1.5,
                          py: 0.9,
                          maxWidth: 320,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          verticalAlign: 'top',
                          backgroundColor:
                            tone === 'added'
                              ? 'rgba(27,158,92,0.06)'
                              : tone === 'removed'
                                ? 'rgba(232,71,73,0.05)'
                                : 'transparent',
                          color: tone === 'removed' ? theme.palette.text.tertiary : 'inherit',
                          borderBottom: `1px solid ${theme.palette.grey[300]}`,
                          borderInlineEnd: `1px solid ${theme.palette.grey[300]}`,
                        }}
                      >
                        {row.cells[col.columnKey] ?? ''}
                      </Box>
                    );
                  })}
                </Box>
              ))
            )}
          </Box>
        </Box>
      </Box>
      </Box>

      {/* Outside the scrollport, so it stays put while the grid scrolls. */}
      {rows.length > MAX_VISIBLE_ROWS && (
        <Typography
          variant="textXS_regular"
          sx={{ color: 'text.tertiary', display: 'block', px: 2, py: 1, flexShrink: 0 }}
        >
          {t('app.remediation.sheet.truncated', {
            shown: MAX_VISIBLE_ROWS,
            total: rows.length,
          })}
        </Typography>
      )}
    </Box>
  );
});
